import { Box, Divider, Icon, Stack, Typography } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { appliedFiltersType } from "../../../../services/api/types/reports/reports";
import { formatKey } from "../utils/helpers";

interface FiltersSectionProps {
  appliedFilters: appliedFiltersType;
  isViewReportMode?: boolean;
  selectedUserNames?: string[];
}

export const FiltersSection: React.FC<FiltersSectionProps> = ({
  appliedFilters,
  isViewReportMode = false,
  selectedUserNames = [],
}) => {
  const { general, userAttributes, assignments } = appliedFilters;

  const userActivityDate =
    general?.UserActivityDate ||
    [general?.UserActivityDateFrom, general?.UserActivityDateTo]
      .filter(Boolean)
      .join(" - ");

  const hasUserAttributes =
    userAttributes && Object.keys(userAttributes).length > 0;
  const hasAssignmentFilters =
    assignments && Object.keys(assignments).length > 0;

  return (
    <Box
      sx={{
        backgroundColor: "#F9FAFB",
        borderRadius: "12px",
        p: 2,
        mb: 3,
        boxShadow: "0px 1px 3px 0px #1018281A",
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <Icon component={FilterAltOutlinedIcon} sx={{ color: "#7F56D9" }} />
        <Typography
          sx={{ color: "#1F2937", fontSize: "14px", fontWeight: 500 }}
        >
          Applied Filters
        </Typography>
      </Stack>
      <Divider sx={{ mt: 1, mb: 1 }} />
      <Box mt={1}>
        <Stack direction="row" gap="118px">
          <Box>
            <Typography fontWeight="600" color="#475467" fontSize="10px">
              Portal:
            </Typography>
            <Typography color="#344054" fontSize="10px">
              {general?.Portal}
            </Typography>
          </Box>
          <Box>
            <Typography fontWeight="600" color="#475467" fontSize="10px">
              Group results by portal:
            </Typography>
            <Typography color="#344054" fontSize="10px">
              {general?.GroupByPortal}
            </Typography>
          </Box>
          {general?.UserActivity && (
            <Box>
              <Typography fontWeight="600" color="#475467" fontSize="10px">
                User activity type:
              </Typography>
              <Typography color="#344054" fontSize="10px">
                {general?.UserActivity}
              </Typography>
            </Box>
          )}
          {userActivityDate && (
            <Box>
              <Typography fontWeight="600" color="#475467" fontSize="10px">
                User activity date:
              </Typography>
              <Typography color="#344054" fontSize="10px">
                {userActivityDate}
              </Typography>
            </Box>
          )}
        </Stack>
        {isViewReportMode ? (
          <>
            <Divider sx={{ mt: 1, mb: 1 }} />
            <Typography fontSize="12px" color="#475467" fontWeight={500} mb={1}>
              Selected users from grid
            </Typography>

            <Typography color="#344054" fontSize="10px">
              {selectedUserNames.length > 0
                ? selectedUserNames.join(", ")
                : "/"}
            </Typography>
          </>
        ) : (
          hasUserAttributes && (
            <>
              <Divider sx={{ mt: 1, mb: 1 }} />
              <Typography
                fontSize="12px"
                color="#475467"
                fontWeight={500}
                mb={1}
              >
                User Attributes
              </Typography>

              <Stack
                direction="row"
                columnGap={9.5}
                rowGap={1}
                sx={{ flexWrap: "wrap" }}
              >
                {Object.entries(userAttributes).map(([key, value]) => (
                  <Box key={key}>
                    <Typography
                      fontWeight="600"
                      color="#475467"
                      fontSize="10px"
                    >
                      {formatKey(key)}:
                    </Typography>
                    <Typography color="#344054" fontSize="10px">
                      {value || "/"}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </>
          )
        )}
        {hasAssignmentFilters && (
          <>
            <Divider sx={{ mt: 1, mb: 1 }} />
            <Typography fontSize="12px" color="#475467" fontWeight={500} mb={1}>
              Assignments
            </Typography>
            <Stack
              direction="row"
              mb={1.5}
              columnGap={9.5}
              rowGap={2}
              sx={{ flexWrap: "wrap" }}
            >
              {Object.entries(assignments).map(([key, value]) => (
                <Box key={key}>
                  <Typography color="#475467" fontWeight={600} fontSize="10px">
                    {formatKey(key)}:
                  </Typography>
                  <Typography color="#475467" fontSize="10px">
                    {value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
};
